import { Button } from '@/components/ui/shadcn/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { cn } from '@/lib/utils';
import { MoreHorizontal } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface ResponsiveActionItem {
    /** Stable unique key. */
    key: string;
    /** Label shown in the overflow dropdown. */
    label: string;
    /** Icon shown in the overflow dropdown. */
    icon?: ReactNode;
    /** The visible control — typically a modal whose `trigger` is a button, or a plain button. */
    node: ReactNode;
    /**
     * Called when the item is chosen from the overflow dropdown. If omitted, the underlying
     * button inside `node` is clicked programmatically (so existing modal triggers keep working).
     */
    onOverflowSelect?: () => void;
    /**
     * Menu entries to render instead of the single one, for a `node` that holds
     * more than one action — a segmented pair, say. Without this the extra
     * actions would be unreachable once the row collapses.
     */
    overflowActions?: Array<{ key: string; label: string; icon?: ReactNode; onSelect: () => void }>;
    /** Renders the overflow menu entry in the destructive colour. */
    destructive?: boolean;
    /**
     * Keeps the item on the row no matter how narrow it gets. Used for the
     * trailing Edit / Transfer / Delete group, which has to stay pinned to the
     * right-hand end rather than being the first thing to disappear.
     */
    pinned?: boolean;
    /**
     * The opposite of `pinned`: keeps the item in the "More" menu however much
     * room there is. For secondary actions that would otherwise crowd the row.
     */
    overflowOnly?: boolean;
}

const GAP = 8; // matches gap-2
const MORE_BUTTON_WIDTH = 40; // icon-sm button + gap reserved for the "More" trigger

/**
 * Renders a row of actions that collapses to fit the available width: as many as fit are shown
 * as buttons, the rest move into a "More" dropdown on the far right. Overflowed items keep their
 * real control mounted (hidden) so modal triggers continue to work when selected from the menu.
 */
export function ResponsiveActions({ items, className }: { items: ResponsiveActionItem[]; className?: string }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const widthCache = useRef<Record<string, number>>({});
    const [visibleCount, setVisibleCount] = useState(items.length);

    const recompute = useCallback(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        // Cache the natural width of every item that is currently in-flow (visible).
        for (const item of items) {
            const el = itemRefs.current[item.key];
            if (el && el.offsetWidth > 0) {
                widthCache.current[item.key] = el.offsetWidth;
            }
        }

        const available = container.clientWidth;
        // Pinned items never overflow, so their width comes off the top; the
        // overflow-only ones never compete for the row in the first place.
        const collapsible = items.filter((item) => !item.pinned && !item.overflowOnly);
        const pinnedWidth = items.filter((item) => item.pinned).reduce((total, item) => total + (widthCache.current[item.key] ?? 0) + GAP, 0);

        // Find the largest prefix of collapsible items that fits, reserving room for the More button when some overflow.
        let best = 0;
        for (let k = collapsible.length; k >= 0; k--) {
            let width = pinnedWidth;
            for (let i = 0; i < k; i++) {
                width += (widthCache.current[collapsible[i].key] ?? 0) + (i > 0 ? GAP : 0);
            }
            if (k < collapsible.length) {
                width += (k > 0 ? GAP : 0) + MORE_BUTTON_WIDTH;
            }
            if (width <= available) {
                best = k;
                break;
            }
        }

        setVisibleCount(best);
    }, [items]);

    useLayoutEffect(() => {
        recompute();
    }, [recompute]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(() => recompute());
        observer.observe(container);
        return () => observer.disconnect();
    }, [recompute]);

    const collapsibleItems = items.filter((item) => !item.pinned && !item.overflowOnly);
    const overflowOnlyItems = items.filter((item) => item.overflowOnly);
    const pinnedItems = items.filter((item) => item.pinned);
    // Menu order follows the array, so an overflow-only item lands wherever the
    // caller placed it relative to the ones that spilled over.
    const overflowItems = items.filter((item) => item.overflowOnly || collapsibleItems.slice(visibleCount).includes(item));

    const handleOverflowSelect = (item: ResponsiveActionItem) => {
        // Defer so the dropdown finishes closing before the (modal) trigger fires.
        window.setTimeout(() => {
            if (item.onOverflowSelect) {
                item.onOverflowSelect();
                return;
            }
            itemRefs.current[item.key]?.querySelector('button')?.click();
        }, 0);
    };

    return (
        <div ref={containerRef} className={cn('relative flex min-w-0 items-center justify-end gap-2', className)}>
            {overflowOnlyItems.map((item) => (
                <div
                    key={item.key}
                    ref={(el) => {
                        itemRefs.current[item.key] = el;
                    }}
                    className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0"
                    aria-hidden
                >
                    {item.node}
                </div>
            ))}
            {collapsibleItems.map((item, index) => {
                const visible = index < visibleCount;
                return (
                    <div
                        key={item.key}
                        ref={(el) => {
                            itemRefs.current[item.key] = el;
                        }}
                        // Hidden overflow items stay mounted (so their trigger can be clicked) but are
                        // taken out of flow so they don't affect the row width.
                        className={cn(visible ? 'flex shrink-0 items-center' : 'pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0')}
                        aria-hidden={!visible}
                    >
                        {item.node}
                    </div>
                );
            })}

            {overflowItems.length > 0 && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button type="button" variant="outline" size="icon-sm" className="shrink-0" aria-label="More actions">
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[200px]">
                        {overflowItems.flatMap((item) =>
                            // A node holding several actions expands to one entry each,
                            // so nothing becomes unreachable when the row collapses.
                            item.overflowActions
                                ? item.overflowActions.map((action) => (
                                      <DropdownMenuItem key={action.key} onSelect={() => window.setTimeout(action.onSelect, 0)}>
                                          <span className="inline-flex items-center gap-2">
                                              {action.icon}
                                              {action.label}
                                          </span>
                                      </DropdownMenuItem>
                                  ))
                                : [
                                      <DropdownMenuItem
                                          key={item.key}
                                          className={cn(item.destructive && 'text-destructive focus:text-destructive')}
                                          // Let the menu close normally, then forward to the (modal) trigger — see handleOverflowSelect.
                                          onSelect={() => handleOverflowSelect(item)}
                                      >
                                          <span className="inline-flex items-center gap-2">
                                              {item.icon}
                                              {item.label}
                                          </span>
                                      </DropdownMenuItem>,
                                  ],
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}

            {/* Pinned tail — Edit / Transfer / Delete — always last on the row. */}
            {pinnedItems.map((item) => (
                <div
                    key={item.key}
                    ref={(el) => {
                        itemRefs.current[item.key] = el;
                    }}
                    className="flex shrink-0 items-center"
                >
                    {item.node}
                </div>
            ))}
        </div>
    );
}
